import Styles from './styles.module.css';
import '../../auth/login/tokens.css';
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppSelector } from 'src/salon/app/hook';
import PhotoTab from './photo/index';
import SessionTab from './session/index';
import CouponTab from './coupon/index';
import StaffTab from './staff/index';
import PaidPlanGate from '../PaidPlanGate';

type TabKey = 'photo' | 'session' | 'coupon' | 'staff';
const TAB_KEYS: TabKey[] = ['photo', 'session', 'coupon', 'staff'];
// 会社説明会(session)は無料プランでも利用可。それ以外は有料限定のため、
// 無料プランではタブは出すが中身をロック案内(PaidPlanGate)に差し替える。
const PAID_ONLY_TABS: TabKey[] = ['photo', 'coupon', 'staff'];

const PAID_GATE_MESSAGE: Record<TabKey, string> = {
  photo: '店舗で作ったヘアスタイル写真を登録し、求人ページの「スタイル」欄で学生に技術や雰囲気を見せられる機能です。',
  session: '',
  coupon: '学生向けの来店クーポンを発行し、サロンを実際に体験してもらうきっかけを作る機能です。',
  staff: 'スタッフのプロフィール（名前・役職・ひとこと・SNS）を登録し、働く人の雰囲気を求人ページで伝えられる機能です。',
};

const TAB_LABELS: Record<TabKey, string> = {
  photo: '作品・写真管理',
  session: '会社説明会',
  coupon: 'クーポン',
  staff: 'スタッフプロフィール',
};

// 各タブの「?」ヒント（段落配列）。ナビの navHint と同じ見せ方。
const TAB_HINTS: Record<TabKey, string[]> = {
  photo: [
    '店舗で作ったヘアスタイルなどの写真を登録する画面です。登録した写真は求人ページの「スタイル」欄に表示されます。',
    'タグ付けしておくと、学生が好みのスタイルから御社を見つけやすくなります。',
  ],
  session: [
    '会社説明会・見学会の日時を登録する画面です。登録すると、学生がアプリから予約できるようになります。',
    '無料プランでも利用できます。日程が決まり次第、早めに登録しておくと参加者が集まりやすくなります。',
  ],
  coupon: [
    '学生向けの来店クーポンを登録する画面です。求人ページに表示され、サロン体験のきっかけになります。',
    '「学生料金」と「通常料金」を入れると、割引額が学生に伝わりやすくなります。',
  ],
  staff: [
    'スタッフのプロフィール（名前・役職・ひとこと・SNS）を登録する画面です。求人ページに表示され、働く人の雰囲気を伝えられます。',
    '人数が多いほど、学生は「どんな先輩がいるか」をイメージしやすくなります。',
  ],
};

// 作品・写真管理(5.photo)・会社説明会(10.session)・クーポン(11.coupon)・
// スタッフ紹介(12.staffProfiles)の4画面を1画面4タブに統合したもの。各タブの
// 中身(Firestore取得・登録・削除処理)は元の画面から移動しただけで、ロジックは
// 変更していない。タブの状態はURLの ?tab= クエリで管理し、リロード・戻る/進む・
// 直接アクセスに対応する。
const SalonPr: React.FC = () => {
  const company = useAppSelector((state) => state.company.company);
  const [ searchParams, setSearchParams ] = useSearchParams();
  const rawTab = searchParams.get('tab');
  // 無料プランは既定タブを「会社説明会」にする（作品タブはロック画面になるため）
  const defaultTab: TabKey = company.contract ? 'photo' : 'session';
  const tab: TabKey = (TAB_KEYS as string[]).includes(rawTab ?? '') ? (rawTab as TabKey) : defaultTab;
  const isLocked = !company.contract && PAID_ONLY_TABS.includes(tab);

  const changeTab = (next: TabKey) => {
    setSearchParams({ tab: next });
  };

  // 「?」ヒントの吹き出し
  const [ hintKey, setHintKey ] = useState<TabKey | null>(null);
  const [ hintPos, setHintPos ] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  const toggleHint = (key: TabKey) => (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    if (hintKey === key) { setHintKey(null); return; }
    const r = e.currentTarget.getBoundingClientRect();
    setHintPos({
      top: Math.min(r.bottom + 8, window.innerHeight - 240),
      left: Math.min(r.left, window.innerWidth - 360),
    });
    setHintKey(key);
  };

  return (
    <div className={Styles.box}>
      <div className={Styles.tabs}>
        {TAB_KEYS.map((key) => (
          <span key={key} className={tab === key ? `${Styles.tabWrap} ${Styles.tabWrapActive}` : Styles.tabWrap}>
            <button
              type="button"
              className={tab === key ? `${Styles.tab} ${Styles.tabActive}` : Styles.tab}
              onClick={() => changeTab(key)}
            >
              {TAB_LABELS[key]}
            </button>
            <span
              role="button"
              tabIndex={0}
              aria-label={`${TAB_LABELS[key]}の説明`}
              className={hintKey === key ? `${Styles.tabHint} ${Styles.tabHintActive}` : Styles.tabHint}
              onClick={toggleHint(key)}
            >?</span>
          </span>
        ))}
      </div>

      {isLocked ? (
        <PaidPlanGate message={PAID_GATE_MESSAGE[tab]} />
      ) : (
        <>
          {tab === 'photo' && <PhotoTab />}
          {tab === 'session' && <SessionTab />}
          {tab === 'coupon' && <CouponTab />}
          {tab === 'staff' && <StaffTab />}
        </>
      )}

      {hintKey && (
        <>
          <div className={Styles.hintScrim} onClick={() => setHintKey(null)} />
          <div className={Styles.hintPopover} style={{ top: hintPos.top, left: hintPos.left }}>
            {TAB_HINTS[hintKey].map((text, i) => (
              <p key={i}>{text}</p>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default SalonPr;
